//(components/all-case-list/case-list-table/CaseListTable)

import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useForm, Controller, clearErrors } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  CaretSortIcon,
  ChevronDownIcon,
} from "@radix-ui/react-icons";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "../../../shadcn-components/ui/button";
import { Checkbox } from "../../../shadcn-components/ui/checkbox";
import PaginationSection from "../../common/PaginationSection";
import TableSkeleton from "../../common/TableSkeleton";
import { createStickyColumns } from "../../common/stickyColumns";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../shadcn-components/ui/dropdown-menu";
import { Input } from "../../../shadcn-components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../shadcn-components/ui/table";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shadcn-components/ui/select";

import mailroomDocs from "../../../api/mailroomDocs";
import { AuthContext } from "../../../context/Auth/AuthContext";
import baseUrl, { sugarUrl } from "../../../config/baseUrl";
import "../../../styles/Clients.css";
import "../../../styles/DocumentTable.css";
import { fileIcon } from "../../common/FileIcons";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../shadcn-components/ui/pagination";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../shadcn-components/ui/tooltip";

import PdfEditor from "../../pdf-viewer/PdfEditor";
import editDocument from "../../../api/editDocument";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../../utils/toast";
import {
  IoArrowBackCircleSharp,
  IoDocumentText,
} from "react-icons/io5";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import SelectBox from "@mui/material/Select";
import { RiCloseCircleFill } from "react-icons/ri";
import Loading from "../../material-ui/Loading";
import ButtonUI from "../../material-ui/ButtonUI";
import ViewerActionButton from "../../common/ViewerActionButton";
import PdfViewer from "../../pdf-viewer/PdfViewer";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CopyLinkPopup from "../case-table/CopyLinkPopup";
import DocumentViewer from "../case-table/DocumentViewer";
import DeleteDocumentPopup from "../case-table/DeleteDocumentPopup";
import EditDocumentPopup from "../case-table/EditDocumentPopup";
import getAllFieldTypeList from "../../../api/getAllFieldTypeList";
import viewDocumentLink from "../../../api/viewDocumentLink";
import pdfBase64Data from "../../../api/pdfBase64Data";
import getAllAssignUsers from "../../../api/getAllAssignUsers";
import AssignUserPopup from "../../email-case-list/case-list-table/AssignUserPopup";
import UnassignUserPopup from "../../email-case-list/case-list-table/UnassignUserPopup";
import BulkDeletePopup from "../../document-table/BulkDeletePopup";
import LinkToRequest from "../../document-table/LinkToRequest";
// Add Task feature disabled (SugarCRM-dependent) - see below for re-enable notes
// import AddTask from "../../document-table/AddTask";
import ApprovePDFDocumentPopup from "../../client-table/client-documents/ApprovePDFDocumentPopup";
import RejectPDFDocumentPopup from "../../client-table/client-documents/RejectPDFDocumentPopup";
import { format } from "date-fns";
import editClient from "../../../api/editClient";
import clientsData from "../../../api/clients";
// get-client-reference-number no longer called - client reference number now comes
// from getCaseStatus (see handleClientNameChange below).
// import getClientRefNo from "../../../api/getClientRefNo";
import DataToolbar from "../../common/DataToolbar";
import { FaDownload, FaLink } from "react-icons/fa";
import { TbCircleCheck } from "react-icons/tb";
import InputUI from "../../material-ui/InputUI";
import getClientName from "../../../api/getClientName";
import useDebounceSearch from "../../../hooks/useDebounceSearch";
import { useDebounce } from "use-debounce";
import elasticToken from "../../../config/elasticToken";
// Send SMS feature disabled (SugarCRM-dependent) - see below for re-enable notes
// import SendSMS from "../../pdf-viewer/SendSMS";
import getClientPhone from "../../../api/getClientPhone";
import { MdError } from "react-icons/md";
import pdfDetails from "../../../api/pdfDetails";
import PdfDetails from "../../pdf-viewer/PdfDetails";
import ZipViewer from "../../pdf-viewer/ZipViewer";
import { useRoleContext } from "../../../context/RolesContext";
import { convertToUTC, formatDate } from "../../../utils/formatDate";
import BusinessCardInfo from "../../common/BusinessCardInfo";
import ConfirmToast from "../../common/ConfirmToast";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import viewDocumentsBulkLink from "../../../api/viewDocumentsBulkLink";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

const updateDocumentDetailsFormSchema = yup.object().shape({
  name: yup.string().required("Document Name is required field"),
  subcategory: yup
    .string()
    .nullable()
    .required("Sub Category is required field"),
});

/*** Component for listing all the mailroom documents for unassinged , all assigned and my assigned sections ***/
const AllDocumentsList = ({
  tableRef,
  caseList,
  addDocumentTab,
  apiLink,
  folderName,
  onOpenDocumentChange,
}) => {
  const [loading, setLoading] = useState(false);

  const { token, user_id, is_admin } = useContext(AuthContext);

  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [reqStatus, setReqStatus] = useState("");
  const [requestId, setRequestId] = useState("");
  const [clientPhone, setClientPhone] = useState(null);

  const [documentUpdated, setDocumentUpdated] = useState(0);
  const [documentDeleted, setDocumentDeleted] = useState(0);
  const [documentViewer, setDocumentViewer] = useState(false);
  // Lets the page hide its header while a document is open (same callback
  // AllDocumentsList and the Email/Fax/Scan CaseListTable already provide).
  useEffect(() => {
    onOpenDocumentChange?.(documentViewer || null);
  }, [documentViewer]); // eslint-disable-line react-hooks/exhaustive-deps
  const [documentId, setDocumentId] = useState("");
  const [pdfLink, setPdfLink] = useState("");
  const [pdfBase64, setPdfBase64] = useState("");
  const [pdfEditor, setPdfEditor] = useState(false);
  const [docFileType, setDocFileType] = useState("");
  const [pdfViewerTitle, setPdfViewerTitle] = useState("");

  const [contactId, setContactId] = useState("");
  const [defaultContactId, setDefaultContactId] = useState("");

  const [benefId, setBenefId] = useState("");
  const [existedContactId, setExistedContactId] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [description, setDescription] = useState("");

  const [bulkAssign, setBulkAssign] = useState(false);
  const [bulkUnassign, setBulkUnassign] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState({
    completed: 0,
    total: 0,
  });
  const [createdAt, setCreatedAt] = useState(null);
  const [documentSourceDetails, setDocumentSourceDetails] = useState(null);
  const [documentSourceTypes, setDocumentSourceTypes] = useState(null);
  const [bulkDeleteDocument, setBulkDeleteDocument] = useState(false);
  const [bulkDeleteDocumentIds, setBulkDeleteDocumentIds] = useState();
  const [bulkDeleteDocumentCounts, setBulkDeleteDocumentCounts] = useState();

  const [subCategoryOption, setSubCategoryOption] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState();
  const [clientNames, setClientNames] = useState([]);

  const [requestOptions, setRequestOptions] = useState([]);
  const [linkRequest, setLinkRequest] = useState(false);
  const [linkRequestOpen, setLinkRequestOpen] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [disapproveConfirmOpen, setDisapproveConfirmOpen] = useState(false);
  const [showLinkButton, setShowLinkButton] = useState(false);
  const [assignClient, setAssignClient] = useState(false);
  const [clientNameLabel, setClientNameLabel] = useState("");
  const [defaultClientNameLabel, setDefaultClientNameLabel] = useState("");
  const [selectedRequestLabel, setSelectedRequestLabel] = useState("");
  const [searchClientNotFound, setSearchClientNotFound] = useState(false);
  const [searchedTerm, setSearchedTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [searchedClientId, setSearchedClientId] = useState([]);
  const [additionalDataFetched, setAdditionalDataFetched] = useState(false);
  const [additionalDataLoading, setAdditionalDataLoading] = useState(false);
  const [isClientListFetched, setIsClientListFetched] = useState(false);
  const [pdfData, setPdfData] = useState([]);
  const [isPdfDetailsContainerOpen, setIsPdfDetailsContainerOpen] =
    useState(false);
  const [pdfDataLoading, setPdfDataLoading] = useState(true);
  const [documentVersionId, setDocumentVersionId] = useState(null);
  const [zipContent, setZipContent] = useState([]);
  const [docData, setDocData] = useState({});
  const [created_At, setCreated_At] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [businessCardInfoClientID, setBusinessCardInfoClientID] = useState("");
  const [confirmUnknownDialogOpen, setConfirmUnknownDialogOpen] =
    useState(false);

  const prevFilterParamsRef = useRef({});
  const prevStateRef = useRef({
    createdAt,
    updatedAt,
    debouncedGlobalFilter,
    columnFilters,
  });

  const [docResLoading, setDocResLoading] = useState(true);

  // Role based permission
  // Function to check if the user has a specific permission.
  // Admin users ("is_admin" === "1") automatically have access to all permissions.
  const { permissions } = useRoleContext();
  const hasPermission = (permission) => {
    if (is_admin === "1") {
      return true;
    }

    return permissions.includes(permission);
  };

  // Permission flags for various user actions, granting access based on user roles or admin status.
  // Admin users have all permissions by default.
  const canCopy = hasPermission("copy_all_documents");
  const canEdit = hasPermission("edit_all_documents");
  const canView = hasPermission("view_all_documents");
  const canDelete = hasPermission("delete_all_documents");
  const canEditPdf = hasPermission("edit_pdf_documents");
  const canLinkDocument = hasPermission("link_documents");
  const canRelinkDocument = hasPermission("relink_document_request");
  const canApproveDocument = hasPermission("document_approval");
  const canEditClient = hasPermission("client_edit");
  const canMarkClientUnknown = hasPermission("mark_client_unknown");
  const canPerformOCR = hasPermission("perform_ocr");
  // Add Task / Send SMS disabled (SugarCRM-dependent) - see usages below
  // const canAddTask = hasPermission("add_task");
  // const canSendSMS = hasPermission("send_sms");
  const canAssign = hasPermission("assign_user");
  const canUnassign = hasPermission("unassign_user");
  const canDownload = hasPermission("download_all_documents");
  const [sugarTokenIssue, setSugarTokenIssue] = useState("");

  const shouldShowActionColumn = canCopy || canEdit || canView || canDelete;

  // Handles marking a client as unknown by calling the appropriate API.
  // Displays a success toast if the operation is successful, otherwise shows an error message.
  // Errors during the process are logged to the console.

  const openClientUnknownDialog = () => {
    setConfirmUnknownDialogOpen(true);
  };

  const confirmMarkClientUnknown = async () => {
    setConfirmUnknownDialogOpen(false);
  };

  const caseListUrls = {
    "all-assigned": "/api/get-assigned-filter-name",
    "all-unassigned": "/api/get-unassigned-filter-name",
    "all-myassigned": "/api/get-myunassigned-filter-name",
  };

  useEffect(() => {
    setIsClientListFetched(false);
    if (clientNames.length > 0) {
      setSearchedClientId(
        clientNames.map((client) => {
          if (client._module === "Contacts") {
            return client.case_id;
          }

          if (client._module === "cpm_CaseParties") {
            return client.id;   // or linked_case_id if your API provides one
          }

          return client.id; // Use id for other modules
        }),
      );
      setIsClientListFetched(true);
    }
  }, [clientNames]);

  const clientIdsString = searchedClientId.join(",");

  // Fetches and updates client data (case status and legal team) for the provided client IDs.
  const fetchAdditionalClientData = async () => {
    setAdditionalDataLoading(true);

    if (!isClientListFetched) {
      console.error("No client IDs found to fetch data.");
      setAdditionalDataLoading(false);
      return;
    }

    try {
      const fetchedData = [];

      const response = await fetch(
        `${baseUrl}/api/getCaseStatus?caseId=${clientIdsString}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data?.caseDetails) {
        data.caseDetails.forEach((caseDetail) => {
          const { status, Lead_Attorney, Legal_Assistant, Paralegal } =
            caseDetail;

          fetchedData.push({
            status,
            Lead_Attorney,
            Legal_Assistant,
            Paralegal,
          });
        });
      }

      setClientNames((prev) =>
        prev.map((client, index) => ({
          ...client,
          ...(fetchedData[index] || {}),
        })),
      );

      setAdditionalDataLoading(false);
      setAdditionalDataFetched(true);
    } catch (error) {
      setAdditionalDataLoading(false);
      console.error("Error fetching additional client data:", error);
    }
  };

  // Fetches and updates the client list based on the search term and offset.
  const getClientList = useCallback(
    async (searchTerm, offsetValue, contactId) => {
      if (offsetValue === 0 && searchedTerm === "") {
        setClientNames([]);
      }
      if (searchTerm) {
        setClientNameLabel("Client Name");
        setSearchLoading(true);
        try {
          const url = `${baseUrl}/api/customGlobalSearch?q=${searchTerm}&max_num=20&offset=${offsetValue}&highlights=true`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const data = await response.json();

          if (data?.total > 0) {
            setClientNames((prev) => [...prev, ...data.records]);
            setTotal(data.total);
            setShowMoreLoading(false);
          } else {
            if (data?.error == "Failed to Search") {
              setSugarTokenIssue(
                "Invalid token for accessing the SugarCRM APIs.",
              );
            } else {
              setSearchClientNotFound(true);
            }
          }
        } catch (error) {
          console.error("Error:", error);
        } finally {
          // Ensure loading states are reset regardless of success or failure
          setSearchLoading(false);
        }

        // const url = `${baseUrl}/api/get-all-client-data?isAdmin=${is_admin}&userId=${user_id}&client_name=${searchTerm}`;
        // const res = await clientsData({ token, api: url });
        // if (res.code === 200) {
        //   setClientNames(res?.data[0]?.clients);
        // } else {
        //   setSearchClientNotFound(true);
        // }
      } else {
        if (!contactId) {
          setClientNameLabel("");
        }
      }
    },
    [],
  );
  const [searchTerm, handleClientNameSearch] = useDebounceSearch(
    "",
    documentId,
    offset,
    contactId,
    2000,
    getClientList,
  );

  // Fetches additional client data when the client list is fetched or client IDs change.
  useEffect(() => {
    if (isClientListFetched || clientIdsString) {
      fetchAdditionalClientData();
    }
  }, [isClientListFetched, clientIdsString]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(updateDocumentDetailsFormSchema),
  });

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    ignoreCase: true, // Ignore case sensitivity
    stringify: (option) => option.fields_name,
  });

  const getClientNameDetails = async (clientId) => {
    if (clientId) {
      const clientNameResponse = await getClientName({
        contact_id: clientId,
        token: token,
      });
      setClientNameLabel(clientNameResponse?.data?.name);
      setDefaultClientNameLabel(clientNameResponse?.data?.name);
    } else {
      setClientNameLabel("");
    }
  };

  const getRequestNameDetails = async (reqId) => {
    const url = `${baseUrl}/api/get-request-name?document_request_id=${reqId}`;
    const res = await clientsData({ token, api: url });
    setSelectedRequestLabel(res.data[0].document_name);
  };

  const fetchSubCategory = async () => {
    try {
      const response = await getAllFieldTypeList({
        token,
        type: "document_subtype",
      });
      setSubCategoryOption(response.data);
    } catch (error) {
      return error;
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setDocResLoading(true);

      const offset = currentPage - 1;
      const filterParams = {};

      columnFilters.forEach((filterItem) => {
        filterParams[filterItem.id] = filterItem.value;
      });

      // Resets to page 1 if filters change and updates the previous filter reference.
      /////////////////////////////////////////

      const filtersChanged =
        JSON.stringify(prevFilterParamsRef.current) !==
        JSON.stringify(filterParams);

      // Reset currentPage if filters changed
      if (filtersChanged) {
        setCurrentPage(1);
      }

      prevFilterParamsRef.current = filterParams;
      /////////////////////////////////////////

      const sortParam = sorting
        .map((sortItem) => {
          return `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`;
        })
        .join(",");

      const params = {
        isAdmin: is_admin,
        userId: user_id,
        limit: pageSize,
        offset: offset,
        ...filterParams,
        global_search: debouncedGlobalFilter,
        sort: sortParam,
        ...(created_At ? { created_at: convertToUTC(created_At) } : {}),
        ...(updatedAt ? { updated_at: convertToUTC(updatedAt) } : {}),
      }; // Adds created_at and updated_at to params if they exist, converting them to UTC.
      const queryString = new URLSearchParams(params).toString();
      const url = `${apiLink}?${queryString}`;

      const response = await mailroomDocs({ token, api: url });

      // If the response is successful, update data and calculate total pages.
      if (response.code == 200) {
        setData(response?.data);
        setTotalPages(Math.ceil(response?.document_count / pageSize));
      } else {
        // If the response fails, reset data and total pages.
        setData([]);
        setTotalPages(0);
      }
    } catch (err) {
      console.error(`An error occured while fetching data, ${err}`);
    } finally {
      setLoading(false);
      setDocResLoading(false);
    }
  };

  const handleLinkRequest = (e) => {
    fetchRequestList(benefId);
    setLinkRequestOpen(true);
  };

  const handleApproveDocument = (e) => {
    setApproveConfirmOpen(true);
  };

  const handleDisapproveDocument = (e) => {
    setDisapproveConfirmOpen(true);
  };

  const handleBulkAction = async (actionType) => {
    const selectedRowsData = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);

    if (actionType === "download") {
      handleBulkDownload(selectedRowsData);
      return;
    }

    const createdAtList = [];
    const documentSourceDetailsList = [];
    const documentSourceTypesList = [];
    const ids = [];
    const documentCounts = [];

    selectedRowsData.forEach((row) => {
      const isValidDate =
        row.created_at !== "0000-00-00" &&
        row.created_at !== "0000-00-00 00:00:00";
      const formattedDate = isValidDate
        ? format(new Date(row.created_at), "yyyy-MM-dd")
        : "N/A";

      if (actionType === "delete") {
        if (row.document_count === 1) {
          ids.push(row.documentID);
        } else {
          createdAtList.push(formattedDate);
          documentSourceDetailsList.push(row.document_source_details);
          documentSourceTypesList.push(row.document_source_type);
        }
        documentCounts.push(row.document_count);
      } else {
        createdAtList.push(formattedDate);
        documentSourceDetailsList.push(row.document_source_details);
        documentSourceTypesList.push(row.document_source_type);
      }
    });

    const createdAt = createdAtList.join(",");
    const documentSourceDetails = documentSourceDetailsList.join(",");
    const documentSourceTypes = documentSourceTypesList.join(",");

    if (actionType === "delete") {
      setBulkDeleteDocument(true);
      setBulkDeleteDocumentIds(ids.join(","));
      setBulkDeleteDocumentCounts(documentCounts.join(","));
    } else if (actionType === "assign") {
      setBulkAssign(true);
    } else {
      setBulkUnassign(true);
    }

    // Shared state updates
    setCreatedAt(createdAt);
    setDocumentSourceDetails(documentSourceDetails);
    setDocumentSourceTypes(documentSourceTypes);
  };

  // Extracts the file extension from a URL, ignoring query params
  const getFileExtensionFromUrl = (url) => {
    try {
      const pathname = new URL(url).pathname;
      const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
      return match ? match[1].toLowerCase() : "";
    } catch {
      const match = url?.split("?")[0]?.match(/\.([a-zA-Z0-9]+)$/);
      return match ? match[1].toLowerCase() : "";
    }
  };

  const handleBulkDownload = async (selectedRowsData) => {
    // Rows with document_count > 1 represent a grouped folder of documents
    // rather than a single document, so documentID doesn't map to a single
    // file in that case. Only single-document rows can be downloaded here.
    const downloadableRows = selectedRowsData.filter(
      (row) => row.document_count === 1,
    );
    const skippedCount = selectedRowsData.length - downloadableRows.length;

    if (!downloadableRows.length) {
      showError(
        "Please select individual documents to download (grouped folders are not supported).",
      );
      return;
    }

    setBulkDownloading(true);
    setBulkDownloadProgress({ completed: 0, total: downloadableRows.length });

    const zip = new JSZip();
    const usedNames = new Set();

    try {
      const docIds = downloadableRows.map((row) => row.documentID);

      const bulkResponse = await viewDocumentsBulkLink({ token, docIds });
      const links = bulkResponse?.data || [];

      if (!links.length) {
        showError(
          "Could not retrieve download links for the selected documents",
        );
        return;
      }

      const rowById = Object.fromEntries(
        downloadableRows.map((row) => [row.documentID, row]),
      );

      await Promise.all(
        links.map(async (linkInfo) => {
          try {
            const { document_id, pdflink } = linkInfo;
            if (!pdflink) {
              console.error(`No download link for document ${document_id}`);
              return;
            }

            const fileResponse = await fetch(
              `${baseUrl}/api/download-document-proxy?document_id=${document_id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!fileResponse.ok) {
              console.error(`Failed to fetch document ${document_id}`);
              return;
            }
            const blob = await fileResponse.blob();

            let fileName =
              rowById[document_id]?.document_name ||
              linkInfo.document_file_name ||
              `document-${document_id}`;

            const extension = getFileExtensionFromUrl(pdflink);
            if (
              extension &&
              !fileName.toLowerCase().endsWith(`.${extension}`)
            ) {
              fileName = `${fileName}.${extension}`;
            }

            if (usedNames.has(fileName)) {
              const dotIndex = fileName.lastIndexOf(".");
              const base =
                dotIndex !== -1 ? fileName.slice(0, dotIndex) : fileName;
              const ext = dotIndex !== -1 ? fileName.slice(dotIndex) : "";
              fileName = `${base}-${document_id}${ext}`;
            }
            usedNames.add(fileName);

            zip.file(fileName, blob);
          } catch (err) {
            console.error(
              `Error processing document ${linkInfo.document_id}:`,
              err,
            );
          } finally {
            // Update regardless of success/failure so the progress count
            // always reaches the total and the overlay doesn't hang.
            setBulkDownloadProgress((prev) => ({
              ...prev,
              completed: prev.completed + 1,
            }));
          }
        }),
      );

      if (Object.keys(zip.files).length === 0) {
        showError("Could not download any of the selected documents");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `documents-${Date.now()}.zip`);

      if (skippedCount > 0) {
        showSuccess(
          `Downloaded ${downloadableRows.length} document(s). Skipped ${skippedCount} grouped folder(s).`,
        );
      } else {
        showSuccess("Documents downloaded successfully");
      }
    } catch (err) {
      showError("Failed to download selected documents");
    } finally {
      setBulkDownloading(false);
      table.resetRowSelection();
      setBulkDownloadProgress({ completed: 0, total: 0 });
    }
  };

  // DMS-local client reference number lookup - uses getCaseStatus (already fetched for
  // the search list) instead of a separate get-client-reference-number call. A genuine
  // request failure still surfaces the error toast; a client simply having no reference
  // number set is not treated as an error.
  const handleClientNameChange = async (contactId) => {
    try {
      const response = await fetch(
        `${baseUrl}/api/getCaseStatus?caseId=${contactId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const data = await response.json();

      if (response.ok && data?.caseDetails) {
        // setClientNameLabel("Client Name");
        setValue("caseId", data?.caseDetails?.[0]?.caseid_c || "");

        setContactId(contactId);
      } else {
        showError(
          "Error while getting client details in DMS. Please contact admin!",
        );
        setValue("caseId", "");
        setContactId("");
      }
    } catch (error) {
      showError(
        "Error while getting client details in DMS. Please contact admin!",
      );
      setValue("caseId", "");
      setContactId("");
    }
    //fetchRequestList(e.target.value);
  };

  const fetchRequestList = async (selectedClient) => {
    setLinkRequest(false);
    //const url = `${baseUrl}/api/get-doc-request-data?userId=1&isAdmin=1&contact_id=${selectedClient}&unlinked=True`;
    const url = `${baseUrl}/api/get-client-document-requests-for-linking?userId=1&isAdmin=1&contact_id=${selectedClient}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data?.code === 200) {
        // Create the additional item
        const additionalItem = {
          document_request_id: "Additional Document",
          document_req_name: "Additional Document",
        };

        // Add it as the first item to the data array
        const updatedData = [
          additionalItem,
          ...data?.data?.document_requests_for_linking,
        ];

        // Set the updated data to the state
        setRequestOptions(updatedData);
      } else {
        // Set only the additional document in the state
        setRequestOptions([
          {
            document_request_id: "Additional Document",
            document_req_name: "Additional Document",
          },
        ]);
      }
    } catch (error) {
      return error;
    }
  };

  const fetchClientPhoneNumber = async (client_id) => {
    try {
      const res = await getClientPhone({ token, client_id });
      setClientPhone(res?.data[0]?.phone_number);
    } catch (err) {}
  };

  useEffect(() => {
    const currentState = {
      created_At,
      updatedAt,
      debouncedGlobalFilter,
      columnFilters,
    };

    // If filters or sorting change, reset page to 1
    if (JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      if (currentPage !== 1) {
        setCurrentPage(1);
        return; // Avoid double API call
      }
    }

    prevStateRef.current = currentState;
    fetchDocuments();

    clearErrors();
    setSelectAllChecked(false);
    if (searchedTerm === "") {
      setClientNames([]);
    }
  }, [
    bulkDeleteDocument,
    documentDeleted,
    documentUpdated,
    debouncedGlobalFilter,
    currentPage,
    columnFilters,
    pageSize,
    sorting,
    documentViewer,
    created_At,
    updatedAt,
  ]);

  useEffect(() => {
    fetchSubCategory();
  }, []);

  useEffect(() => {
    if (assignClient) {
      fetchRequestList(benefId);
    }
  }, [assignClient]);

  useEffect(() => {
    const caseIdValue = getValues("caseId");
    const selectedSubCategoryId = getValues("subcategory");

    const selectedSubCategory = subCategoryOption?.find(
      (option) => option.field_setting_id === selectedSubCategoryId,
    );
    const subcategoryLabel = selectedSubCategory
      ? selectedSubCategory.fields_name
      : "";

    setDocData({
      ...docData,
      contact_id: contactId,
      phone_number: clientPhone,
      ben_id: benefId,
      document_desc: description,
      client_reference_number: caseIdValue || "",
      document_subtype: subcategoryLabel,
      document_sub_category: selectedSubCategoryId,
    });
    contactId && !linkRequest && !reqStatus
      ? setShowLinkButton(true)
      : setShowLinkButton(false);
  }, [requestOptions, reqStatus]);

  useEffect(() => {
    setOffset(0);
    setTotal(0);
  }, [searchedTerm]);

  // Functions to check if a file is a specific type (PDF, ZIP, document, or image) based on its extension.
  const isFileType = (src, validExtensions) => {
    const extension = src?.split(".").pop().toLowerCase();
    return validExtensions.includes(extension);
  };

  const isPDF = (src) => isFileType(src, ["pdf", "txt"]);
  const isZip = (src) => isFileType(src, ["zip"]);
  const isDoc = (src) =>
    isFileType(src, ["doc", "docx", "xls", "xlsx", "ppt", "pptx"]);
  const isImage = (src) =>
    isFileType(src, ["jpg", "jpeg", "png", "gif", "bmp", "svg"]);
  const isMp4 = (src) => isFileType(src, ["mp4"]);

  // Downloads the document by creating a temporary link element.
  const handleDocumentDownload = function () {
    const link = document.createElement("a");
    link.href = pdfLink;
    link.target = "_blank";
    link.download = pdfViewerTitle || "download";
    link.click();
  };

  // Converts a base64 string to a Blob object with the specified MIME type.
  const base64ToBlob = (base64, mimeType) => {
    const binaryString = atob(base64); // Decode the base64 string
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  };

  // Converts the PDF's Base64 data to a Blob and updates the zip content when pdfBase64 changes.
  useEffect(() => {
    if (pdfBase64 && pdfBase64[0]?.data && isZip(pdfLink)) {
      const blob = base64ToBlob(pdfBase64[0]?.data, "application/zip");

      setZipContent(blob);
    }
  }, [pdfBase64]);

  let columns;
  if (caseList === "all-unassigned") {
    columns = [
      {
        id: "select",
        header: ({ table }) => (
          <div className="flex items-center">
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
            {(table.getIsSomePageRowsSelected() ||
              table.getIsAllPageRowsSelected()) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="p-0 !border-none !shadow-none bg-transparent"
                  >
                    <ChevronDownIcon className="" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => handleBulkAction("download")}
                    disabled={!canDownload || bulkDownloading}
                  >
                    {bulkDownloading ? "Downloading..." : "Download"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleBulkAction("delete")}
                    disabled={!canDelete}
                  >
                    Delete
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleBulkAction("assign")}
                    disabled={!canAssign}
                  >
                    Assign
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              setSelectAllChecked(true);
              row.toggleSelected(!!value);
            }}
            aria-label="Select row"
            //disabled={row.original.document_count > 1}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "document_type",
        displayName: "Type",
        filterable: true,
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="p-0"
            >
              Type
              <CaretSortIcon className="ml-2 h-6" />
            </Button>
          );
        },
        cell: ({ row }) => (
          <div className="w-full flex items-center">
            {row.original.document_count == 1
              ? fileIcon[row.getValue("document_type").toLowerCase()]
              : fileIcon["folder"]}
          </div>
        ),
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "client_name",
        displayName: "Client Name",
        filterable: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Client Name
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const clientName = row.getValue("client_name");
          const clientUnknown = row.original.client_unknown === "1";
          const clientKnown = row.original.client_unknown === "0";
          const docCount = row.getValue("document_count");

          // if (docCount > 1) {
          //   return;
          // }

          return (
            <div className="flex items-center gap-0.5">
              <div className="mr-2">
                {clientUnknown ? (
                  <span>
                    <TbCircleCheck color="#eab308" className="h-6 w-6" />
                  </span>
                ) : clientName || clientKnown ? (
                  <span>
                    <TbCircleCheck color="#3b82f6" className="h-6 w-6" />
                  </span>
                ) : null}
              </div>
              <span className="wrap-text capitalize">
                {clientUnknown ? "Unknown" : clientName}
              </span>
            </div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "phone_number",
        displayName: "Phone No",
        filterable: false,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Phone No
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const phone_no = row.getValue("phone_number");

          return <span className="">{phone_no}</span>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
        enableHiding: false,
      },
      {
        accessorKey: "document_source_type",
        displayName: "Source",
        filterable: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Source
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const document_source_type =
            row.getValue("document_source_type") == "Scanned"
              ? "Scan"
              : row.getValue("document_source_type");

          return <div className="capitalize">{document_source_type}</div>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "document_source_details",
        displayName: "Source Details",
        filterable: true,
        header: ({ column }) => (
          <Button
            className="capitalize p-0"
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Source Details
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const sourceDetails =
            caseList == "email" ||
            caseList == "all-unassigned" ||
            caseList == "all-myassigned" ||
            caseList == "all-assigned"
              ? row.getValue("document_source_details")
              : row.original.documentID;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-gray-800 font-medium">
                    {sourceDetails && sourceDetails.length > 15
                      ? `${sourceDetails.substring(0, 15)}...`
                      : sourceDetails || ""}
                  </span>
                </TooltipTrigger>
                {sourceDetails && sourceDetails.length > 15 && (
                  <TooltipContent className="bg-gray-600">
                    <p>{sourceDetails}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "email_subject",
        displayName: "Subject",
        filterable: false,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Subject
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const email_subject = row.getValue("email_subject");

          return <span className="">{email_subject}</span>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
        enableHiding: false,
      },
      {
        accessorKey: "document_name",
        displayName: "File Name",
        filterable: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            File Name
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const document_name =
            row.original.document_count == 1
              ? row.getValue("document_name")
              : "All";
          const document_count = row.getValue("document_count");
          const document_type = row.getValue("document_type");

          const documentID = row.original.documentID;

          return (
            <div
              className={`capitalize text-left w-3 text-wrap ${
                canView
                  ? "cursor-pointer text-blue-500  underline"
                  : "cursor-not-allowed text-gray-500"
              }`}
              onClick={() => {
                if (!canView) return;
                setDocData(row.original);
                setDocData(row.original);

                if (tableRef.current) {
                  tableRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
                if (document_count > 1) {
                  addDocumentTab(row.original);
                } else {
                  changeDocumentViewerState(
                    row.original.client_reference_number,
                    row.original.contact_id,
                    row.original.document_name,
                    row.original.category,
                    row.original.document_sub_category,
                    row.original.document_desc,
                    row.original.documentID,
                    row.original.document_type,
                    row.original.phone_number,
                    row.original.ben_id,
                  );
                }
              }}
            >
              {document_name}
            </div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "created_at",
        displayName: "Created At",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Created At
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const uploaded = row.getValue("created_at");
          return (
            <div className="capitalize text-wrap">{formatDate(uploaded)}</div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "updated_at",
        displayName: "Modified At",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Modified At
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const updated = row.getValue("updated_at");
          return (
            <div className="capitalize text-wrap">{formatDate(updated)}</div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "modified_user",
        displayName: "Modified By",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Modified By
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const modified_user = row.getValue("modified_user");
          return <div className="capitalize text-wrap">{modified_user}</div>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "document_count",
        displayName: "Actions",
        header: ({ column }) => {
          if (!shouldShowActionColumn) {
            return null;
          }
          return (
            <Button variant="ghost" size="sm" className="p-0 !cursor-default">
              Actions
            </Button>
          );
        },
        cell: ({ row }) => {
          if (row.getValue("document_count") > 1) {
            return (
              <div
                className="flex gap-2 cursor-pointer text-[#0481c1d0]"
                onClick={() => addDocumentTab(row.original)}
              >
                {row.getValue("document_count")} Documents
              </div>
            );
          } else {
            return (
              <div className="flex gap-1.5">
                {canEdit && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span>
                          <EditDocumentPopup
                            tableValues={row.original}
                            subCategoryOption={subCategoryOption}
                            setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Edit</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {row?.original?.contact_id === null && canCopy ? (
                  <CopyLinkPopup tableValues={row.original} />
                ) : (
                  canCopy && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <span>
                            <CopyLinkPopup tableValues={row.original} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-600">
                          <p>Copyss</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )
                )}

                {canView && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span>
                          <DocumentViewer
                            tableValues={row.original}
                            changeDocumentViewerState={
                              changeDocumentViewerState
                            }
                            setDocData={setDocData}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>View</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {canDelete && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span>
                          <DeleteDocumentPopup
                            tableValues={row.original}
                            setDocumentDeletedToTrue={setDocumentDeletedToTrue}
                            setRowSelection={setRowSelection}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Delete</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            );
          }
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
      },
    ];
  } else {
    columns = [
      {
        id: "select",
        header: ({ table }) => (
          <div className="flex items-center">
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
            {(table.getIsSomePageRowsSelected() ||
              table.getIsAllPageRowsSelected()) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="p-0 !border-none !shadow-none bg-transparent"
                  >
                    <ChevronDownIcon className="" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => handleBulkAction("download")}
                    disabled={!canDownload || bulkDownloading}
                  >
                    {bulkDownloading ? "Downloading..." : "Download"}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleBulkAction("delete")}
                    disabled={!canDelete}
                  >
                    Delete
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleBulkAction("assign")}
                    disabled={!canAssign}
                  >
                    Assign
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleBulkAction("unassign")}
                    disabled={!canUnassign}
                  >
                    Unassign
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              setSelectAllChecked(true);
              row.toggleSelected(!!value);
            }}
            //disabled={row.original.document_count > 1}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "document_type",
        displayName: "Type",
        filterable: true,
        header: ({ column }) => {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="p-0"
            >
              Type
              <CaretSortIcon className="ml-2 h-6" />
            </Button>
          );
        },
        cell: ({ row }) => (
          <div className="w-full flex items-center">
            {row.original.document_count == 1
              ? fileIcon[row.getValue("document_type").toLowerCase()]
              : fileIcon["folder"]}
          </div>
        ),
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "client_name",
        displayName: "Client Name",
        filterable: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Client Name
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const clientName = row.getValue("client_name");
          const clientUnknown = row.original.client_unknown === "1";
          const clientKnown = row.original.client_unknown === "0";
          const docCount = row.getValue("document_count");

          // if (docCount > 1) {
          //   return;
          // }
          return (
            <div className="flex items-center gap-0.5">
              <div className="mr-2">
                {clientUnknown ? (
                  <span>
                    <TbCircleCheck color="#eab308" className="h-6 w-6" />
                  </span>
                ) : clientName || clientKnown ? (
                  <span>
                    <TbCircleCheck color="#3b82f6" className="h-6 w-6" />
                  </span>
                ) : null}
              </div>
              <span className="wrap-text capitalize">
                {clientUnknown ? "Unknown" : clientName}
              </span>
            </div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "phone_number",
        displayName: "Phone No",
        filterable: false,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Phone No
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const phone_no = row.getValue("phone_number");

          return <span className="">{phone_no}</span>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
        enableHiding: false,
      },
      {
        accessorKey: "document_source_type",
        displayName: "Source",
        filterable: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Source
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const document_source_type =
            row.getValue("document_source_type") == "Scanned"
              ? "Scan"
              : row.getValue("document_source_type");

          return <div className="capitalize">{document_source_type}</div>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "document_source_details",
        displayName: "Source Details",
        filterable: true,
        header: ({ column }) => (
          <Button
            className="capitalize p-0"
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Source Details
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const sourceDetails =
            caseList == "email" ||
            caseList == "all-unassigned" ||
            caseList == "all-myassigned" ||
            caseList == "all-assigned"
              ? row.getValue("document_source_details")
              : row.original.documentID;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="text-gray-800 font-medium">
                    {sourceDetails && sourceDetails.length > 15
                      ? `${sourceDetails.substring(0, 15)}...`
                      : sourceDetails || ""}
                  </span>
                </TooltipTrigger>
                {sourceDetails && sourceDetails.length > 15 && (
                  <TooltipContent className="bg-gray-600">
                    <p>{sourceDetails}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "email_subject",
        displayName: "Subject",
        filterable: false,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Subject
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const email_subject = row.getValue("email_subject");

          return <span className="">{email_subject}</span>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
        enableHiding: false,
      },
      {
        accessorKey: "document_name",
        displayName: "File Name",
        filterable: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            File Name
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const document_name =
            row.original.document_count == 1
              ? row.getValue("document_name")
              : "All";
          const document_count = row.getValue("document_count");
          const document_type = row.getValue("document_type");
          const documentID = row.original.documentID;

          return (
            <div
              className={`capitalize text-left w-3 text-wrap ${
                canView
                  ? "cursor-pointer text-blue-500  underline"
                  : "cursor-not-allowed text-gray-500"
              }`}
              onClick={() => {
                if (!canView) return;

                setDocData(row.original);
                setDocData(row.original);

                if (document_count > 1) {
                  addDocumentTab(row.original);
                  tableRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                } else {
                  changeDocumentViewerState(
                    row.original.client_reference_number,
                    row.original.contact_id,
                    row.original.document_name,
                    row.original.category,
                    row.original.document_sub_category,
                    row.original.document_desc,
                    row.original.documentID,
                    row.original.document_type,
                    row.original.phone_number,
                    row.original.ben_id,
                  );
                }
              }}
            >
              {document_name}
            </div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "assign_user",
        displayName: "Assigned User",
        filterable: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Assigned User
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const assign_user = row.getValue("assign_user")
            ? row.getValue("assign_user")
            : "Unassigned";

          return <div className="capitalize">{assign_user}</div>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },

      {
        accessorKey: "created_at",
        displayName: "Created",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Created
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const uploaded = row.getValue("created_at");
          return (
            <div className="capitalize text-wrap">{formatDate(uploaded)}</div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "updated_at",
        displayName: "Modified At",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Modified At
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const updated = row.getValue("updated_at");
          return (
            <div className="capitalize text-wrap">{formatDate(updated)}</div>
          );
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "modified_user",
        displayName: "Modified By",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Modified By
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const modified_user = row.getValue("modified_user");
          return <div className="capitalize text-wrap">{modified_user}</div>;
        },
        filterFn: (row, id, value) => {
          return value.includes(row.getValue(id));
        },
      },
      {
        accessorKey: "document_count",
        displayName: "Actions",
        header: ({ column }) => {
          if (!shouldShowActionColumn) {
            return null;
          }
          return (
            <Button variant="ghost" size="sm" className="p-0 !cursor-default">
              Actions
            </Button>
          );
        },
        cell: ({ row }) => {
          if (row.getValue("document_count") > 1) {
            return (
              <div
                className="flex gap-2 cursor-pointer text-[#0481c1d0]"
                onClick={() => addDocumentTab(row.original)}
              >
                {row.getValue("document_count")} Documents
              </div>
            );
          } else {
            return (
              <div className="flex gap-1.5">
                {canEdit && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span>
                          <EditDocumentPopup
                            tableValues={row.original}
                            subCategoryOption={subCategoryOption}
                            setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Edit</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {row?.original?.contact_id === null && canCopy ? (
                  <CopyLinkPopup tableValues={row.original} />
                ) : (
                  canCopy && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <span>
                            <CopyLinkPopup tableValues={row.original} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-600">
                          <p>Copy</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )
                )}

                {canView && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span>
                          <DocumentViewer
                            tableValues={row.original}
                            changeDocumentViewerState={
                              changeDocumentViewerState
                            }
                            setDocData={setDocData}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>View</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {canDelete && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span>
                          <DeleteDocumentPopup
                            tableValues={row.original}
                            setDocumentDeletedToTrue={setDocumentDeletedToTrue}
                            setRowSelection={setRowSelection}
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Delete</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            );
          }
        },
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: false,
      },
    ];
  }

  // Pins the leading "select"/"Type" columns to the left edge and the
  // trailing "Actions" (document_count) column to the right edge, so only
  // the data-heavy middle columns scroll horizontally. Widths are fixed so
  // the header and body cells for a given column line up at the same
  // left/right offset.
  const { getStickyCellProps } = createStickyColumns({
    left: { select: 48, document_type: 70 },
    right: ["document_count"],
  });

  const table = useReactTable({
    data: data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),

    initialState: {
        pageSize: 20,
    },

    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    // Do NOT apply client-side filtering
    // getFilteredRowModel: getFilteredRowModel(),

    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),

    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,

    state: {
        sorting,
        columnFilters,
        columnVisibility,
        rowSelection,
        pageSize,
    },


    globalFilterFn: (rows, columnFilters) => {
      const [globalFilter] = columnFilters.filter(
        (filter) => filter.id === "global",
      );
      if (!globalFilter || !globalFilter.value) return rows;

      return rows.filter((row) => {
        const name = row.original.name.toLowerCase(); // Assuming 'name' is the column key
        return name.includes(globalFilter.value.toLowerCase());
      });
    },
    onGlobalFilterChange: (filterValue) => {
      setGlobalFilter(filterValue); // Update the global filter state
    },
  });

  const setDocumentUpdatedToTrue = () => {
    setDocumentUpdated((count) => count + 1);
  };

  const setDocumentDeletedToTrue = () => {
    setDocumentDeleted((count) => count + 1);
  };

  const changeActiveTab = () => {
    setDocumentViewer(false);
    setPdfEditor(false);
  };

  const changeDocumentViewerState = async (
    caseID,
    contactId,
    name,
    category,
    subCategory,
    desc,
    id,
    type,
    client_phone,
    benId,
  ) => {
    setTotal(0);
    setAssignClient(false);
    setClientPhone(client_phone);
    setApproveConfirmOpen(false);
    setDisapproveConfirmOpen(false);
    setLoading(true);
    setSelectedRequestLabel("");
    const response = await viewDocumentLink({
      token: token,
      docId: id,
    });

    if (response?.code === 400) {
      toast.error(response?.message, {
        position: "top-center",
        autoClose: 5000,
        theme: "dark",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
      throw new Error(response?.message);
    }

    const data = await response?.data[0];

    const link = data?.pdflink;
    setRequestId(data?.document_request_id);
    setReqStatus(data?.document_status);
    setExistedContactId(contactId);
    setContactId(contactId);
    setDefaultContactId(contactId);
    setValue("addClientName", contactId);

    if (benId) {
      setBenefId(benId);
      fetchRequestList(benId); // Call with benId if it exists
    } else {
      setBenefId(contactId);
      fetchRequestList(contactId); // Call with contact_id if benId is not available
    }

    if (data?.document_request_id) {
      getRequestNameDetails(data?.document_request_id);
    }

    setValue("caseId", caseID);
    setValue("name", name);
    setValue("category", category);
    setSubCategory(subCategory);
    setValue("subcategory", subCategory);
    setDescription(desc);
    setDocumentId(id);
    setPdfLink(link);
    setDocFileType(type);
    // if (type == "pdf") {
    const responseBase64Data = await pdfBase64Data(token, id, "documents");
    const base64Data = await responseBase64Data?.data;
    setPdfBase64(base64Data);
    // }
    setPdfViewerTitle(name);
    setDocumentViewer(true);
    if (benId) {
      getClientNameDetails(benId); // Call with benId if it exists
    } else {
      getClientNameDetails(contactId); // Call with contact_id if benId is not available
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!clientNameLabel) {
      setValue("addClientName", null);
      setValue("caseId", "");
    }
    if (clientNameLabel === defaultClientNameLabel) {
      setValue("addClientName", defaultContactId);
      if (defaultContactId && defaultContactId !== null) {
        handleClientNameChange(defaultContactId);
      }
    }
  }, [clientNameLabel, getValues("addClientName")]);

  // useEffect(() => {
  //   if (benefId) {
  //     getClientNameDetails(benefId);
  //   } else {
  //     getClientNameDetails(contactId);
  //   }
  // }, [pdfEditor]);

  const onEditDocumentFormSubmit = async (editDocumentFormData) => {
    //if (!oldClientRefNo && clientRefNo) {
    // await editClient({
    // 	contact_id: contactId,
    // 	token: token,
    // 	user_id: user_id,
    // 	client_reference_number: editDocumentFormData.caseId,
    // });
    // }

    const editDocumentData = {
      token: token,
      user_id: user_id,
      document_id: documentId,
      document_desc: description,
      document_name: editDocumentFormData.name,
      document_sub_category: editDocumentFormData.subcategory,
      contact_id: editDocumentFormData.addClientName,
    };

    // If a client name label is provided, set it as the default label
    // and mark the client as known by updating the "client_unknown" flag to "0".
    if (clientNameLabel) {
      setDefaultClientNameLabel(clientNameLabel);
      editDocumentData.client_unknown = "0";
    }

    const editDocumentFormResponse = await editDocument(editDocumentData);

    if (editDocumentFormResponse.code === 200) {
      // const urlAddDocumentRequests = `${baseUrl}/api/add-document-requests?contact_id=${editDocumentFormData.addClientName}`;
      // try {
      //   const responseAddDocumentRequests = await fetch(
      //     urlAddDocumentRequests,
      //     {
      //       method: "POST",
      //       headers: {
      //         Accept: "application/json",
      //         Authorization: `Bearer ${token}`,
      //       },
      //     }
      //   );

      //   await responseAddDocumentRequests.json();
      // } catch (error) {
      //   return error;
      // }

      fetchClientPhoneNumber(editDocumentFormData.addClientName);
      setPdfViewerTitle(editDocumentFormData.name);
      setAssignClient(true);
      setExistedContactId(editDocumentFormData.addClientName);
      showSuccess("Document is updated successfully");
    } else {
      showError("Error while updating document");
    }
  };

  const changePdfDetailsContainerVisibility = () => {
    setIsPdfDetailsContainerOpen(!isPdfDetailsContainerOpen);
  };
  const pdfDetailsData = async () => {
    setLoading(true);
    setPdfDataLoading(true);

    try {
      const response = await pdfDetails(
        token,
        docData?.documentID,
        "documents",
      );
      const data = await response.data;

      setPdfData(data);
      setPdfLink(data?.pdflink);
    } catch (error) {
      console.error("Error fetching PDF details:", error);
    } finally {
      setLoading(false);
      setPdfDataLoading(false);
    }
  };

  const pdfDetailsVersionData = async () => {
    try {
      const response = await pdfDetails(
        token,
        docData?.documentID,
        "documents",
        documentVersionId,
      );
      const data = await response.data;

      setPdfData(data);
      setPdfLink(data[0]?.pdflink);
    } catch (error) {
      console.error("Error fetching PDF details:", error);
    } finally {
      setLoading(false);
      setPdfDataLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch PDF details when a document is actually selected
    if (docData?.documentID) {
      pdfDetailsData();
    }
  }, []);

  // Helper Component for Unsupported Files
  const UnsupportedFileMessage = ({ onDownload }) => (
    <div className="bg-[#ddeef6] border-l-4 border-[#44a0cf] text-white p-6 rounded-lg shadow-md max-w-md mx-auto flex flex-col justify-center items-center">
      <span className="text-gray-800 text-base font-semibold mb-3 text-center">
        This file is not supported for viewing here. You can download it using
        the button below.
      </span>
      <button
        onClick={onDownload}
        className="bg-[#2b93c8d0] hover:bg-[#44a0cf] py-2 px-4 rounded-lg flex gap-1 justify-center items-center self-center transition-all duration-300"
      >
        <FaDownload /> Download
      </button>
    </div>
  );

  const normalizeFileUrl = (url) =>
    url ? url.replace(/\.video\/(3gp|mp4|mov|avi|mkv|webm)$/i, ".$1") : url;

  // Returns the appropriate viewer based on the file type.
  const getViewer = (pdfLink) => {
    if (isPDF(pdfLink)) {
      return (
        <iframe
          title={pdfViewerTitle}
          src={pdfLink}
          width="100%"
          height="590"
          key={pdfLink}
        />
      );
    }

    if (isDoc(pdfLink)) {
      return (
        <iframe
          title={pdfViewerTitle}
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${pdfLink}`}
          // src={`https://docs.google.com/gview?url=${pdfLink}&embedded=true`} //googleDocs
          className="w-full h-full border-none"
        />
      );
    }

    if (isMp4(pdfLink)) {
      return (
        <div className="flex justify-center items-center w-full max-w-[800px]">
          <video
            controls
            className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
          >
            <source src={pdfLink} type="video/mp4" />
          </video>
        </div>
      );
    }

    if (isZip(pdfLink)) {
      return <ZipViewer zipContent={zipContent} zipSrc={pdfLink} />;
    }

    if (isImage(pdfLink)) {
      return (
        <div
          style={{
            width: "100%",
            maxHeight: "590px", // limit visible area
            overflow: "auto", // scroll if image is taller
          }}
        >
          <img src={pdfLink} width="100%" height="590" alt={pdfViewerTitle} />
        </div>
      );
    }

    // Default for unsupported files
    return <UnsupportedFileMessage onDownload={handleDocumentDownload} />;
  };

  //Setting the clientId for the Info Card
  const clientInfoID = function () {
    const addClientNameValue = getValues("addClientName");
    if (
      addClientNameValue !== null &&
      addClientNameValue !== undefined &&
      addClientNameValue !== ""
    ) {
      setBusinessCardInfoClientID(addClientNameValue);
    } else if (clientNameLabel === defaultClientNameLabel) {
      setBusinessCardInfoClientID(defaultContactId);
    } else {
      setBusinessCardInfoClientID(defaultContactId);
    }

    if (clientNameLabel === defaultClientNameLabel) {
      setBusinessCardInfoClientID(defaultContactId);
    }
  };
  useEffect(() => {
    clientInfoID();
  }, [contactId, getValues("addClientName"), clientNameLabel]);

  return (
    <>
      <ConfirmToast
        open={confirmUnknownDialogOpen}
        onClose={() => setConfirmUnknownDialogOpen(false)}
        onConfirm={confirmMarkClientUnknown}
        title="Mark Client as Unknown"
        message="Are you sure you want to mark the client as unknown?"
        documentId={documentId}
        setDocData={setDocData}
        setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
      />

      {approveConfirmOpen && (
        <ApprovePDFDocumentPopup
          documentId={documentId}
          documentRequestId={requestId}
          setReqStatus={setReqStatus}
          setDocumentViewer={setDocumentViewer}
          setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
          setApproveConfirmOpen={setApproveConfirmOpen}
        />
      )}
      {disapproveConfirmOpen && (
        <RejectPDFDocumentPopup
          documentRequestId={requestId}
          setReqStatus={setReqStatus}
          setDocumentViewer={setDocumentViewer}
          setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
          setDisapproveConfirmOpen={setDisapproveConfirmOpen}
        />
      )}
      {linkRequestOpen && (
        <LinkToRequest
          documentId={documentId}
          requestOptions={requestOptions}
          setLinkRequest={setLinkRequest}
          setReqStatus={setReqStatus}
          setRequestId={setRequestId}
          setLinkRequestOpen={setLinkRequestOpen}
          setSelectedRequestLabel={setSelectedRequestLabel}
          setDocumentViewer={setDocumentViewer}
          setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
          pdfLink={pdfLink}
          documentRequestId={requestId}
        />
      )}

      {bulkDeleteDocument ? (
        <BulkDeletePopup
          setDocumentDeletedToTrue={setDocumentDeletedToTrue}
          Docbulkid={bulkDeleteDocumentIds}
          bulkDeleteDocument={bulkDeleteDocument}
          setBulkDeleteDocument={setBulkDeleteDocument}
          setRowSelection={setRowSelection}
          bulkDeleteDocumentCounts={bulkDeleteDocumentCounts}
          documentSourceTypes={documentSourceTypes}
          createdAt={createdAt}
          documentSourceDetails={documentSourceDetails}
          section={caseList}
        />
      ) : null}
      {bulkAssign ? (
        <AssignUserPopup
          selectedRows={table
            ?.getFilteredSelectedRowModel()
            ?.rows.map((row) => row.original)}
          setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
          bulkAssign={bulkAssign}
          setBulkAssign={setBulkAssign}
          caseList={documentSourceTypes}
          createdAt={createdAt}
          documentSourceDetails={documentSourceDetails}
          setRowSelection={setRowSelection}
        />
      ) : null}
      {bulkUnassign ? (
        <UnassignUserPopup
          selectedRows={table
            ?.getFilteredSelectedRowModel()
            ?.rows.map((row) => row.original)}
          setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
          bulkUnassign={bulkUnassign}
          setBulkUnassign={setBulkUnassign}
          caseList={documentSourceTypes}
          createdAt={createdAt}
          documentSourceDetails={documentSourceDetails}
          setRowSelection={setRowSelection}
        />
      ) : null}
      {bulkDownloading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex flex-col items-center gap-3 rounded-lg bg-white px-8 py-6 shadow-lg">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            <p className="text-sm font-medium text-gray-700">
              Preparing your download...
            </p>
          </div>
        </div>
      )}
      {!documentViewer ? (
        <>
          <div className="document-table documents-table-page">
            <div className="documents-content">
              <div className="document__table documents-table-layout">
                <div className="documents-table-surface">
                  <div className="documents-toolbar flex justify-between items-center flex-wrap gap-4">
                    <Input
                      placeholder="Search..."
                      value={globalFilter}
                      onChange={(event) =>
                        table.setGlobalFilter(event.target.value)
                      }
                      className="max-w-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className=""
                        onClick={() => setShowFilterButton(!showFilterButton)}
                      >
                        <FilterListIcon sx={{ fontSize: 18 }} className="mr-1" />
                        Filter
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="ml-auto">
                            <ViewColumnIcon sx={{ fontSize: 18 }} className="mr-1" />
                          Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {table
                            .getAllColumns()
                            .filter((column) => column.getCanHide())
                            .map((column) => {
                              return (
                                <DropdownMenuCheckboxItem
                                  key={column.id}
                                  className="capitalize"
                                  checked={column.getIsVisible()}
                                  onCheckedChange={(value) =>
                                    column.toggleVisibility(!!value)
                                  }
                                >
                                  {column.columnDef.displayName}
                                </DropdownMenuCheckboxItem>
                              );
                            })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {showFilterButton && (
                    <DataToolbar
                      table={table}
                      apiUrl={
                        caseListUrls[caseList] ||
                        "/api/get-assigned-filter-name"
                      }
                      createdAt={created_At}
                      updatedAt={updatedAt}
                      setCreatedAt={setCreated_At}
                      setUpdatedAt={setUpdatedAt}
                    />
                  )}
                  <div className="documents-table-frame rounded-md border">
                    {loading ? (
                      <TableSkeleton />
                    ) : (
                      <div className="w-full overflow-x-auto">
                        <Table className="w-full table-auto">
                          <TableHeader className="sticky top-0 bg-white z-10">
                            {table.getHeaderGroups().map((headerGroup) => (
                              <TableRow key={headerGroup.id} className="">
                                {headerGroup.headers.map((header) => {
                                  const sticky = getStickyCellProps(
                                    header.column.id,
                                    true,
                                  );
                                  return (
                                    <TableHead
                                      key={header.id}
                                      style={sticky.style}
                                    >
                                      {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                          )}
                                    </TableHead>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableHeader>
                          <TableBody>
                            {table.getRowModel().rows?.length ? (
                              table.getRowModel().rows.map((row) => (
                                <TableRow
                                  key={row.id}
                                  data-state={row.getIsSelected() && "selected"}
                                  className=""
                                >
                                  {row.getVisibleCells().map((cell) => {
                                    const sticky = getStickyCellProps(
                                      cell.column.id,
                                    );
                                    return (
                                      <TableCell
                                        key={cell.id}
                                        className="py-1 px-2 text-xs truncate"
                                        style={sticky.style}
                                      >
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext(),
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))
                            ) : (
                              <TableRow className="">
                                <TableCell
                                  colSpan={columns.length}
                                  className="h-24 text-center "
                                >
                                  {loading || docResLoading ? (
                                    <TableSkeleton />
                                  ) : (
                                    "No records found"
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                  <div className="documents-pagination flex items-center justify-between space-x-2 flex-wrap gap-4">
                    <div className="flex items-center gap-5">
                      <div className="flex-1 text-sm text-muted-foreground">
                        {table.getFilteredSelectedRowModel().rows.length} of{" "}
                        {table.getFilteredRowModel().rows.length} row(s)
                        selected.
                      </div>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium">Rows per page</p>
                        <Select
                          value={`${table.getState().pageSize}`}
                          onValueChange={(value) => {
                            setPageSize(Number(value));
                            setCurrentPage(1);
                          }}
                        >
                          <SelectTrigger className="w-auto">
                            <SelectValue
                              placeholder={table.getState().pageSize}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {[10, 20, 30, 40, 50].map((pageSize) => (
                                <SelectItem
                                  key={pageSize}
                                  value={`${pageSize}`}
                                >
                                  {pageSize}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-x-2">
                      <PaginationSection
                        setCurrentPage={setCurrentPage}
                        totalPages={totalPages}
                        currentPage={currentPage}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Same shared document viewer as every other document page
        // (case-table/AllDocumentsList uses identical props), instead of the
        // older duplicated inline viewer this list used to render.
        <DndProvider backend={HTML5Backend}>
          <PdfViewer
            documentType={docFileType}
            clientID={contactId}
            documentID={documentId}
            documentTitle={pdfViewerTitle}
            documentURL={pdfLink}
            documentCreatedAt={docData?.created_at}
            document_table="documents"
            documentRequestID={requestId}
            existingDocumentRequestStatus={reqStatus}
            documentCategory={docData?.category}
            documentDesc={docData?.document_desc}
            documentSubCategory={docData?.document_sub_category}
            clientPhone={docData?.phone_number}
            benID={docData?.ben_id}
            currentTabId={documentId}
            closeTabById={changeActiveTab}
            changeActiveTab={changeActiveTab}
            fromTable="AllDocumentsList"
            refetchDocumentTable={fetchDocuments}
          />
        </DndProvider>
      )}
    </>
  );
};

export default AllDocumentsList;
